import * as React from 'react'
import { useMemo, useState } from 'react'
import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses, Text, Tooltip } from '@fluentui/react-components'
import PersonAvatar from '../../components/PersonAvatar'
import { Link } from 'react-router-dom'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { getMissingTaskDates, getTaskDateLabelKey, isDurationVisible, TaskDateName } from '../../helpers/taskDateHelper'
import {
    AttachRegular,
    bundleIcon,
    CheckmarkFilled,
    CheckmarkRegular,
    FlagFilled,
    FlagRegular,
    FlowchartFilled,
    ImportantFilled,
    ImportantRegular,
    NewFilled,
    NewRegular,
    SettingsFilled,
    SettingsRegular,
    ShiftsActivityFilled,
    ShiftsActivityRegular,
} from '@fluentui/react-icons'
import useTaskStatuses from '../../hooks/useTaskStatuses'
import { BaseTaskOverviewLineItemProps, useItemStyles } from './BaseTaskOverview'
import { TaskStatus } from '../../context/GlobalContext'
import DocumentLink from '../../components/Document/DocumentLink'
import { useTranslation } from 'react-i18next'
import useAxios from '../../hooks/useAxios'
import useTasks from '../../hooks/useTasks'
import TimeText from '../../components/Text/Time/TimeText'
import { ViewType } from '../BaseOverview/BaseOverviewFilter'
import TaskDates from './TaskDates'
import useUsers from '../../hooks/useUsers'
import { ActiveUserStatuses } from '../../config/userConfig'
import TooltippedText from '../../v2/components/Text/TooltippedText'
import TooltippedButton from '../../v2/components/Button/TooltippedButton'

const SettingsIcon = bundleIcon(SettingsFilled, SettingsRegular)
const NewIcon = bundleIcon(NewFilled, NewRegular)
const CheckmarkIcon = bundleIcon(CheckmarkFilled, CheckmarkRegular)
const ShiftsActivityIcon = bundleIcon(ShiftsActivityFilled, ShiftsActivityRegular)

export function getStatusIcon(status: TaskStatus) {
    if (status.isStartStatus) {
        return <NewIcon />
    } else if (status.isEndStatus) {
        return <CheckmarkIcon />
    } else {
        return <ShiftsActivityIcon />
    }
}

export function getFlagIcon(props: BaseTaskOverviewLineItemProps, itemStyles: Record<'filterTagToday' | 'filterTagWeek', string>) {
    if (props.item.today && props.item.week) {
        if (props.filterValues.today) {
            return <FlagFilled className={itemStyles.filterTagToday} />
        } else if (props.filterValues.week) {
            return <FlagFilled className={itemStyles.filterTagWeek} />
        }
    }
    if (props.item.today) {
        return <FlagFilled className={itemStyles.filterTagToday} />
    } else if (props.item.week) {
        return <FlagFilled className={itemStyles.filterTagWeek} />
    }

    return <SettingsIcon />
}

function TaskListItemAssignee(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const { users } = useUsers()

    return (
        <Menu hasIcons>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`${translate('task.filter.assignee')}: ${props.item.assignee?.fullName}`} withArrow>
                    <div className={itemStyles.listTaskItemMenuButton} onClick={e => e.stopPropagation()}>
                        <PersonAvatar tooltip={false} name={props.item.assignee?.fullName} color={'colorful'} size={24} />
                    </div>
                </Tooltip>
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {users
                        .filter(entity => props.item.assignee?.id === entity.id || ActiveUserStatuses.includes(entity.status))
                        .map((user, index) => (
                            <MenuItem
                                key={index}
                                onClick={e => {
                                    e.stopPropagation()
                                    axios
                                        .post(`/task/${props.item.id}/assignee`, user.id, {
                                            headers: { 'Content-Type': 'text/plain' },
                                        })
                                        .then(() =>
                                            props.onUpdateItem(props.item, [
                                                props.columnIndex,
                                                props.filterValues.statuses.indexOf(props.item.status),
                                            ]),
                                        )
                                }}
                            >
                                {user.fullName}
                            </MenuItem>
                        ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

function TaskListItemStatus(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const { taskStatuses, getStatus, getStatusName } = useTaskStatuses()

    const userStatusIcon = getStatusIcon(getStatus(props.item.status))

    return (
        <Menu hasIcons>
            <MenuTrigger disableButtonEnhancement>
                <TooltippedButton
                    appearance="subtle"
                    display={'iconOnly'}
                    size={'small'}
                    icon={userStatusIcon}
                    tooltip={`${translate('task.filter.status.label')}: ${getStatusName(props.item.status)}`}
                />
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {taskStatuses.map((status, index) => (
                        <MenuItem
                            key={index}
                            icon={getStatusIcon(status)}
                            onClick={e => {
                                e.stopPropagation()
                                axios
                                    .post(`/task/${props.item.id}/status`, status.alpha, {
                                        headers: { 'Content-Type': 'text/plain' },
                                    })
                                    .then(() =>
                                        props.onUpdateItem(props.item, [props.columnIndex, props.filterValues.statuses.indexOf(status.alpha)]),
                                    )
                            }}
                        >
                            {getStatusName(status.alpha)}
                        </MenuItem>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

function TaskListItemActions(props: BaseTaskOverviewLineItemProps) {
    const axios = useAxios()
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Menu hasIcons open={open} onOpenChange={(_e, data) => setOpen(data.open)}>
            <MenuTrigger disableButtonEnhancement>
                <Button
                    size={'small'}
                    appearance={'subtle'}
                    icon={getFlagIcon(props, itemStyles)}
                    aria-label={
                        props.item.today ? 'flag-today-active' : props.item.week ? 'flag-week-active' : open ? 'flag-action-active' : 'flag-action'
                    }
                />
            </MenuTrigger>
            <MenuPopover>
                <MenuList>
                    <MenuItem
                        icon={props.item.highlight ? <ImportantFilled className={itemStyles.filterTagHighlight} /> : <ImportantRegular />}
                        onClick={e => {
                            e.stopPropagation()
                            axios.post(`/task/${props.item.id}/highlight`).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }}
                    >
                        {translate(props.item.highlight ? 'task.nothighlight' : 'task.highlight')}
                    </MenuItem>
                    <MenuItem
                        icon={props.item.week ? <FlagFilled className={itemStyles.filterTagWeek} /> : <FlagRegular />}
                        onClick={e => {
                            e.stopPropagation()
                            axios.post(`/task/${props.item.id}/week`).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }}
                    >
                        {translate(props.item.week ? 'task.notweek' : 'task.week')}
                    </MenuItem>
                    <MenuItem
                        icon={props.item.today ? <FlagFilled className={itemStyles.filterTagToday} /> : <FlagRegular />}
                        onClick={e => {
                            e.stopPropagation()
                            axios.post(`/task/${props.item.id}/today`).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
                        }}
                    >
                        {translate(props.item.today ? 'task.nottoday' : 'task.today')}
                    </MenuItem>
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

export function TaskListItemAttachments(props: BaseTaskOverviewLineItemProps) {
    const [showMenu, setShowMenu] = useState<boolean>(false)

    if (props.item.attachmentIds?.length === 0) {
        return <div></div>
    }

    return (
        <Menu positioning={{ autoSize: true }} open={showMenu} onOpenChange={(_, { open }) => setShowMenu(open)}>
            <MenuTrigger disableButtonEnhancement>
                <Button icon={<AttachRegular />} appearance="transparent" size={'small'} onClick={() => setShowMenu(!showMenu)} />
            </MenuTrigger>

            <MenuPopover>
                <MenuList>{showMenu && props.item.attachmentIds?.map((attachment, index) => <DocumentLink key={index} id={attachment} />)}</MenuList>
            </MenuPopover>
        </Menu>
    )
}

export function TaskListItemWorkflow(props: BaseTaskOverviewLineItemProps) {
    const itemStyles = useItemStyles()

    return (
        <>
            {props.item.workflowStepName && (
                <TooltippedButton
                    appearance={'transparent'}
                    display={'iconOnly'}
                    size={'small'}
                    icon={<FlowchartFilled />}
                    className={itemStyles.workflowColor}
                    tooltip={props.item.workflowStepName}
                />
            )}
            {!props.item.workflowStepName && <div></div>}
        </>
    )
}

export function TaskListItemFile(props: BaseTaskOverviewLineItemProps) {
    const overviewStyles = useOverviewStyles()

    return (
        <>
            {!props.fileId ? (
                <Tooltip relationship={'label'} content={props.item.file.name} withArrow>
                    <Link key={props.item.id} to={`/file/${props.item.file.id}`} className={overviewStyles.alignEnd}>
                        <Text size={200}>{props.item.file.reference}</Text>
                    </Link>
                </Tooltip>
            ) : (
                <div></div>
            )}
        </>
    )
}

function TaskListItemExpectedTime(props: BaseTaskOverviewLineItemProps) {
    const { dateConfig } = useTasks()

    // An empty duration is 0:00; whether that is worth showing is up to the office (HAM-4286, criterion 22).
    return dateConfig.duration.enabled && isDurationVisible(props.item.duration, dateConfig) ? (
        <div>
            <TimeText value={props.item.duration || '0:00'} />
        </div>
    ) : (
        <div></div>
    )
}

function TaskListItemCategory(props: BaseTaskOverviewLineItemProps) {
    const { categories, getCategoryById } = useTasks()
    const overviewStyles = useOverviewStyles()

    return categories?.length > 0 && props.item.category ? (
        <TooltippedText className={overviewStyles.alignEnd} tooltip={getCategoryById(props.item.category)?.description}>
            {getCategoryById(props.item.category)?.name}
        </TooltippedText>
    ) : (
        <div></div>
    )
}

/**
 * The task is missing a date the office requires (HAM-4286, criterion 20). That happens with tasks that predate the
 * setting and with workflow tasks, which never get blocked. The task stays usable; the bar says what is missing and
 * the date itself is filled in through the task form.
 */
function TaskListItemMissingDates(props: BaseTaskOverviewLineItemProps & { missing: TaskDateName[] }) {
    const itemStyles = useItemStyles()
    const { t: translate } = useTranslation()
    const { dateConfig } = useTasks()

    const names = props.missing.map(name => translate(getTaskDateLabelKey(name, dateConfig))).join(', ')

    return (
        <div className={itemStyles.listTaskItemMissingDates}>
            <ImportantFilled />
            <Text size={200}>{translate('task.view.datemissing', { names })}</Text>
        </div>
    )
}

function TaskListItemDates(props: BaseTaskOverviewLineItemProps & { compact: boolean }) {
    const axios = useAxios()
    const { dateConfig } = useTasks()

    return (
        <TaskDates
            dates={{ start: props.item.start, due: props.item.due }}
            config={dateConfig}
            compact={props.compact}
            setDates={dates => {
                axios.post(`/task/${props.item.id}/dates`, dates).then(() => props.onUpdateItem(props.item, [props.columnIndex]))
            }}
        />
    )
}

export default function TaskListItem(props: BaseTaskOverviewLineItemProps) {
    const overviewStyles = useOverviewStyles()
    const itemStyles = useItemStyles()
    const { viewport, isViewport } = useViewport()
    const { dateConfig } = useTasks()

    const listItemSmall = useMemo<boolean>(
        () => props.viewType === ViewType.KanbanView || isViewport('<', Viewport.OutlookNewWide),
        [viewport, props.viewType],
    )
    // In the Outlook task pane two dates plus the action icons no longer fit on one line, so the dates take a line of
    // their own there. Nothing is dropped or truncated; the card is only one line taller (HAM-4286, criteria 23-24).
    const datesOnOwnRow = useMemo<boolean>(() => dateConfig.twoDates && isViewport('<=', Viewport.OutlookNew), [viewport, dateConfig.twoDates])
    const missingDates = useMemo(() => getMissingTaskDates(props.item, dateConfig), [props.item.start, props.item.due, dateConfig])

    if (listItemSmall) {
        return (
            <>
                {missingDates.length > 0 && <TaskListItemMissingDates {...props} missing={missingDates} />}
                <div className={itemStyles.listTaskItemSmallRow1}>
                    <TaskListItemAssignee {...props} />
                    <TooltippedText className={itemStyles.listTaskItemOverflow} richTooltip tooltip={props.item.description}>
                        {props.item.title}
                    </TooltippedText>
                </div>
                <div className={mergeClasses(itemStyles.listTaskItemSmallRow2, overviewStyles.alignItemsStart)}>
                    <TaskListItemExpectedTime {...props} />
                    <TaskListItemAttachments {...props} />
                    <TaskListItemCategory {...props} />
                </div>
                {datesOnOwnRow && (
                    <div className={itemStyles.listTaskItemSmallDateRow}>
                        <TaskListItemDates {...props} compact />
                    </div>
                )}
                <div className={mergeClasses(itemStyles.listTaskItemSmallRow3, datesOnOwnRow && itemStyles.listTaskItemSmallRow3WithoutDates)}>
                    {!datesOnOwnRow && <TaskListItemDates {...props} compact />}
                    <TaskListItemStatus {...props} />
                    <TaskListItemActions {...props} />
                    <TaskListItemWorkflow {...props} />
                    <TaskListItemFile {...props} />
                    <PersonAvatar name={props.item.assigner.fullName} color={'colorful'} size={24} />
                </div>
            </>
        )
    }

    return (
        <>
            {missingDates.length > 0 && <TaskListItemMissingDates {...props} missing={missingDates} />}
            <TaskListItemAssignee {...props} />
            <TaskListItemStatus {...props} />
            <TaskListItemDates {...props} compact={false} />

            <div className={itemStyles.listTaskItemTitle}>
                <TaskListItemExpectedTime {...props} />
                <TooltippedText className={itemStyles.listTaskItemOverflow} richTooltip tooltip={props.item.description}>
                    {props.item.title}
                </TooltippedText>
                <TaskListItemCategory {...props} />
            </div>
            <TaskListItemActions {...props} />
            <TaskListItemAttachments {...props} />
            <TaskListItemWorkflow {...props} />
            <TaskListItemFile {...props} />
            <PersonAvatar name={props.item.assigner?.fullName} color={'colorful'} size={24} />
        </>
    )
}
